import { TabStyles } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { BridgeSiteSettingsProps } from '~src/settings/bridge-settings/BridgeSiteSettings';
import { DomainUpdateType, IUpdateEdgePoolResult } from '~src/settings/bridge-settings/serverApi/requests/UpdateEdgePoolRequest';
import { EdgePool } from '~src/settings/bridge-settings/serverApi/resources/EdgePool';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { componentRegistryEntry } from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import { cssClass } from '~src/style';

import { DomainID } from '../types/Domain';
import { DomainPermission } from '../types/DomainPermission';
import { DomainTableItem } from '../types/DomainTableItem';
import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { PoolID, SystemDefinedPoolID } from '../types/PoolID';
import { createDomainTableItems, getDomainFromDomainID, getPoolIDForDomainID } from '../utils/DomainUtilities';
import { getErrorMessage } from '../utils/EdgePoolErrorUtilities';
import { createPoolOptionsFromPools } from '../utils/PoolModelUtilities';
import { AddNewDomainButton } from './AddNewDomainButton';
import { openAddNewDomainDialog } from './addNewDomainDialog/AddNewDomainDialog';
import { openEditDomainDialog } from './addNewDomainDialog/EditDomainDialog';
import { BridgeNetworkAllowlistInformation } from './BridgeNetworkAllowlistInformation';
import { openDeleteDomainConfirmationDialog } from './deleteDomainConfirmationDialog/DeleteDomainConfirmationDialog';
import { DomainTable } from './DomainTable';

export const BridgeNetworkAllowlistSpecs = componentRegistryEntry({
  name: 'BridgeNetworkAllowlist',
  component: BridgeNetworkAllowlist
});

export function BridgeNetworkAllowlist(props: BridgeSiteSettingsProps) {
  const { refreshEdgePools, hasLoadedEdgePools } = props;
  const poolOptions: ReadonlyArray<PoolAssignmentOption> = React.useMemo(() => createPoolOptionsFromPools(props.edgePools.userDefinedPools), [props.edgePools.userDefinedPools]);
  const domainTableItems: ReadonlyArray<DomainTableItem> = createDomainTableItems(props.edgePools.userDefinedPools, props.edgePools.blockedDomains);

  const onAddNewDomainClicked = React.useCallback(() => {
    const onAddDomainDialogSave = (domainName: string, poolId: PoolID | null, domainPermission: DomainPermission) => {
      const targetPoolId = domainPermission === DomainPermission.ALLOW ? poolId : SystemDefinedPoolID.Blocked;
      EdgePool.updatePool({
        poolId: targetPoolId,
        displayName: null,
        domainChange: { change: DomainUpdateType.Add, domainId: domainName as DomainID },
        agentChange: null,
        siteId: props.edgePools.siteId,
        bridgeSettingsVersion: '0'
      })
        .then((result: IUpdateEdgePoolResult) => {
          refreshEdgePools();
          if (result.success) {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.bridgeSiteSettingsAddDomainSuccess() });
          }
          if (result.error) {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: getErrorMessage(result.error.errorCode, {poolName: null, domainName: domainName, agentName: null})
            });
          }
        });
    };
    openAddNewDomainDialog(onAddDomainDialogSave, poolOptions, domainTableItems.map((item: DomainTableItem) => item.domainName));
  }, [domainTableItems, poolOptions, props.edgePools.siteId, refreshEdgePools]);

  const onEditDomainClicked = React.useCallback((domainId: DomainID) => {
    const onEditDomainDialogSave = (domainName: string, poolId: PoolID | null, domainPermission: DomainPermission) => {
      const targetPoolId = domainPermission === DomainPermission.ALLOW ? poolId : SystemDefinedPoolID.Blocked;
      EdgePool.updatePool({
        poolId: getPoolIDForDomainID(props.edgePools.userDefinedPools, props.edgePools.blockedDomains, domainId),
        displayName: null,
        domainChange: { change: DomainUpdateType.Remove, domainId: domainId as DomainID },
        agentChange: null,
        siteId: props.edgePools.siteId,
        bridgeSettingsVersion: '0'
      }).then((resultForRemove: IUpdateEdgePoolResult) => {
        if (resultForRemove.error) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getErrorMessage(resultForRemove.error.errorCode, {poolName: null, domainName: domainId, agentName: null})
          });
        }
        if (resultForRemove.success) {
          EdgePool.updatePool({
            poolId: targetPoolId,
            displayName: null,
            domainChange: { change: DomainUpdateType.Add, domainId: domainName as DomainID },
            agentChange: null,
            siteId: props.edgePools.siteId,
            bridgeSettingsVersion: '0'
          })
            .then((resultForAdd: IUpdateEdgePoolResult) => {
              refreshEdgePools();
              if (resultForAdd.success) {
                ToasterService.instance.pop({ type: ToastType.Success, text: Messages.bridgeSiteSettingsEditDomainSuccess() });
              }
              if (resultForAdd.error) {
                ToasterService.instance.pop({
                  type: ToastType.Error,
                  text: getErrorMessage(resultForAdd.error.errorCode, {poolName: null, domainName: domainName, agentName: null})
                });
              }
            });
        }
      });
    };
    const domain = getDomainFromDomainID(props.edgePools.userDefinedPools, props.edgePools.blockedDomains, domainId);
    const domainEditableProperties = {
      domainName: domain.domainName,
      poolID: getPoolIDForDomainID(props.edgePools.userDefinedPools, props.edgePools.blockedDomains, domainId),
      domainPermission: domain.domainPermission,
    };
    openEditDomainDialog(domainEditableProperties, onEditDomainDialogSave, poolOptions, domainTableItems.map((item: DomainTableItem) => item.domainName));
  }, [domainTableItems, poolOptions, props.edgePools.siteId, props.edgePools.userDefinedPools, refreshEdgePools, props.edgePools.blockedDomains]);

  const onDeleteDomainClicked = React.useCallback((domainId: string, poolId: PoolID) => {
    const deleteDomainCallback = () => {
      EdgePool.updatePool({
        poolId: poolId,
        displayName: null,
        domainChange: { change: DomainUpdateType.Remove, domainId: domainId as DomainID },
        agentChange: null,
        siteId: props.edgePools.siteId,
        bridgeSettingsVersion: '0'
      })
        .then((result: IUpdateEdgePoolResult) => {
          refreshEdgePools();
          if (result.success) {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.bridgeSiteSettingsDeleteDomainSuccess() });
          }
          if (result.error) {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: getErrorMessage(result.error.errorCode, {poolName: null, domainName: domainId, agentName: null})
            });
          }
        });
    };
    openDeleteDomainConfirmationDialog(deleteDomainCallback);
  }, [props.edgePools.siteId, refreshEdgePools]);

  if (!FeatureFlags.edgeMultiPoolEnabled) {
    return <></>;
  }
  return (
    <SettingsSection title={Messages.bridgeSiteSettingsPrivateNetworkAllowlist()} testId={'bridge-network-allowlist'}>
      <div className={subHeaderClass}>{Messages.bridgeSiteSettingsAllowlistRegistry()}</div>
      <BridgeNetworkAllowlistInformation />
      <AddNewDomainButton onAddNewDomainClicked={onAddNewDomainClicked} hasLoadedEdgePools={hasLoadedEdgePools} />
      <DomainTable
        domainItems={domainTableItems}
        onEditDomainClicked={onEditDomainClicked}
        onDeleteDomainClicked={onDeleteDomainClicked}
        hasLoadedEdgePools={hasLoadedEdgePools}
      />
    </SettingsSection>
  );
}

const subHeaderClass = cssClass('bridge-network-allowlist', 'header', {
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'bold',
  paddingBottom: TabStyles.Spacing.MediumSpace
});

